/**
 * InstagramAppDownload — post Instagram 1080×1350 (4:5).
 *
 * iter v32 r45 · RESET COMPLETO · User M1442: «rivedi tutto, rendila figa».
 *
 * Direzione decisa (niente compromessi):
 *  · Stile coerente con i post @prontoimmo.it esistenti
 *  · UN solo blocco titolo Montserrat ALL CAPS BOLD (no più mix serif italic)
 *  · iPhone 17 Pro con la VERA schermata sopralluogo dell'app
 *  · 3 bullet essenziali (non 9)
 *  · Cream pulito, texture sottile, accent sage/caramel mirati
 *  · Composizione asimmetrica · text-left + phone-right
 *  · Header identico ai vostri post · footer identico
 */
import React from "react";

const APP_STORE_URL = "https://apps.apple.com/it/app/id6777127700";

const SERVICES = [
  { label: "Annuncio pronto", hint: "Dal vocale, già scritto da copiare." },
  { label: "Foto perfette", hint: "Luce, ordine, restyling, arredo virtuale." },
  { label: "Sito personale", hint: "La tua vetrina online, attiva subito.", featured: true },
  { label: "Post & Reel Instagram", hint: "Pronti col tuo logo, da pubblicare." },
  { label: "Stima online del cliente", hint: "Form sul tuo sito, contatto diretto." },
  { label: "Agenda vocale", hint: "Appuntamenti dettati a voce." },
  { label: "WhatsApp marketing", hint: "Messaggi e auguri ai tuoi clienti." },
  { label: "CRM contatti", hint: "Tutti i clienti e lo storico in un posto." },
  { label: "Pubblicazione portali", hint: "Estensione Chrome che compila Idealista e Immobiliare." },
];

export default function InstagramAppDownload() {
  const [scale, setScale] = React.useState(0.42);
  const [exporting, setExporting] = React.useState(false);

  // Export mode: ?clean=1 disabilita wrapper dark e mostra canvas a scale 1
  // ?reel=1 forza formato 9:16 (1080×1920) per IG Reel/Story con cream
  // esteso sopra e sotto (niente bande nere).
  const cleanMode = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("clean") === "1";
  }, []);
  const reelMode = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("reel") === "1";
  }, []);

  React.useEffect(() => {
    if (cleanMode) {
      setScale(1);
      return;
    }
    const recalc = () => {
      const maxW = Math.min(window.innerWidth - 24, 620);
      const maxH = window.innerHeight - 24;
      const sw = maxW / 1080;
      const sh = maxH / 1350;
      setScale(Math.min(sw, sh));
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [cleanMode]);

  // Scarica PNG client-side via html2canvas
  const downloadPng = async () => {
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvasEl = document.querySelector(
        '[data-testid="ig-post-canvas"]'
      );
      if (!canvasEl) return;
      const png = await html2canvas(canvasEl, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
        width: 1080,
        height: 1350,
      });
      const link = document.createElement("a");
      link.download = "pronto-scarica-app.png";
      link.href = png.toDataURL("image/png");
      link.click();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PNG export failed", e);
      alert("Export PNG fallito. Riprova.");
    } finally {
      setExporting(false);
    }
  };

  // Scarica MP4 server-side via /api/share/scarica-app/video
  const downloadMp4 = async () => {
    setExporting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(
        `${apiBase}/api/share/scarica-app/video?format=reel`,
        { method: "GET" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = "pronto-scarica-app-reel.mp4";
      link.href = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("MP4 export failed", e);
      alert("Export MP4 fallito. Il video viene rigenerato lato server e può richiedere qualche secondo.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className={
        cleanMode
          ? "min-h-screen flex items-center justify-center bg-[#F5F0E2]"
          : "min-h-screen flex items-center justify-center bg-[#0E0F0D] py-3"
      }
      style={{ fontFamily: "'Inter','Helvetica Neue',sans-serif" }}
    >
      {/* Animazioni · entrance + loop ambient */}
      <style>{`
        @keyframes pronto-fadeUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pronto-fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes pronto-phoneIn {
          0% { opacity: 0; transform: translateY(40px) rotate(2deg); }
          100% { opacity: 1; transform: translateY(0) rotate(-3deg); }
        }
        @keyframes pronto-phoneFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pronto-accentGrow {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes pronto-iconReveal {
          0% { opacity: 0; transform: scale(0.32); }
          70% { opacity: 1; transform: scale(1.06); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pronto-iconPulseAfter {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes pronto-screenBgWash {
          0% { background: #0A0B09; }
          100% { background: linear-gradient(180deg, #F4F1E8 0%, #EDE9DC 50%, #E5E0CF 100%); }
        }
        .anim-up {
          opacity: 0;
          animation: pronto-fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .anim-up-slow {
          opacity: 0;
          animation: pronto-fadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .anim-in {
          opacity: 0;
          animation: pronto-fadeIn 0.9s ease-out forwards;
        }
        .anim-accent {
          transform-origin: left;
          transform: scaleX(0);
          animation: pronto-accentGrow 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .phone-entrance {
          opacity: 0;
          animation: pronto-phoneIn 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .phone-float {
          animation: pronto-phoneFloat 5s ease-in-out 2.4s infinite;
        }
        .app-icon-reveal {
          opacity: 0;
          transform-origin: center;
          animation: pronto-iconReveal 0.95s cubic-bezier(0.34, 1.56, 0.64, 1) 1.9s forwards;
        }
        .app-icon-pulse-after {
          animation: pronto-iconPulseAfter 3.2s ease-in-out 3.2s infinite;
          transform-origin: center;
        }
        .app-screen-wash {
          background: #0A0B09;
          animation: pronto-screenBgWash 0.6s ease-out 1.55s forwards;
        }
        .app-label-reveal {
          opacity: 0;
          animation: pronto-fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 2.4s forwards;
        }
      `}</style>

      <div
        style={{
          width: 1080 * scale,
          height: 1350 * scale,
          position: "relative",
          overflow: "hidden",
          borderRadius: cleanMode ? 0 : 8,
          boxShadow: cleanMode ? "none" : "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div
          data-testid="ig-post-canvas"
          className="relative overflow-hidden"
          style={{
            width: 1080,
            height: 1350,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            fontFamily: "'Inter','Helvetica Neue',sans-serif",
            background: "#F5F0E2",
          }}
        >
          {/* ───── Texture · paper grain ───── */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: 0.07,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
          {/* Soft sage halo top-right */}
          <div
            aria-hidden
            className="absolute"
            style={{
              top: -240,
              right: -200,
              width: 620,
              height: 620,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at center, rgba(124,139,110,0.20) 0%, rgba(124,139,110,0) 70%)",
            }}
          />
          {/* Soft caramel halo bottom-left */}
          <div
            aria-hidden
            className="absolute"
            style={{
              bottom: -300,
              left: -220,
              width: 660,
              height: 660,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at center, rgba(181,124,94,0.14) 0%, rgba(181,124,94,0) 70%)",
            }}
          />

          {/* ═══════════════════════════════════════════
              HEADER · identico ai post IG
              ═══════════════════════════════════════════ */}
          <div className="absolute top-[54px] left-[60px] right-[60px] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/logo/mark-symbol-256.png"
                alt=""
                style={{ width: 48, height: 48 }}
                className="object-contain"
              />
              <div
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 30,
                  fontWeight: 700,
                  color: "#1C1C1A",
                  letterSpacing: "-0.015em",
                  lineHeight: 1,
                }}
              >
                Pronto
              </div>
            </div>
            <div
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 17,
                fontWeight: 500,
                color: "#1C1C1A",
                letterSpacing: "-0.005em",
                textAlign: "right",
                lineHeight: 1.25,
              }}
            >
              Un&apos;unica app
              <br />
              <span style={{ color: "#5A5A52", fontWeight: 400 }}>
                per l&apos;agente immobiliare
              </span>
            </div>
          </div>

          {/* Hairline divider */}
          <div
            aria-hidden
            className="absolute"
            style={{
              top: 126,
              left: 60,
              right: 60,
              height: 1,
              background: "rgba(28,28,26,0.16)",
            }}
          />

          {/* ═══════════════════════════════════════════
              EYEBROW + ACCENT
              ═══════════════════════════════════════════ */}
          <div
            className="absolute anim-up"
            style={{ top: 170, left: 60, animationDelay: "0.1s" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "#B57C5E",
                  boxShadow: "0 0 0 5px rgba(181,124,94,0.16)",
                }}
              />
              <span
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.36em",
                  color: "#1C1C1A",
                  textTransform: "uppercase",
                }}
              >
                Scarica l&apos;app · iOS 17+
              </span>
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              TITOLO · HERO LANDING · "Da un vocale..."
              ═══════════════════════════════════════════ */}
          <div
            className="absolute"
            style={{ top: 210, left: 60, width: 600 }}
          >
            <h1
              className="anim-up"
              style={{
                margin: 0,
                fontFamily: "'Tinos','Times New Roman',serif",
                fontWeight: 400,
                fontSize: 90,
                lineHeight: 0.96,
                letterSpacing: "-0.03em",
                color: "#1C1C1A",
                animationDelay: "0.25s",
              }}
            >
              Da un{" "}
              <span style={{ fontStyle: "italic", color: "#7C8B6E" }}>
                vocale
              </span>
              <br />e qualche{" "}
              <span style={{ fontStyle: "italic", color: "#B57C5E" }}>
                scatto.
              </span>
            </h1>

            {/* Sub-claim caramel · "Al resto ci pensa Pronto." */}
            <div
              className="anim-up"
              style={{
                marginTop: 18,
                fontFamily: "'Tinos','Times New Roman',serif",
                fontStyle: "italic",
                fontSize: 36,
                lineHeight: 1.15,
                letterSpacing: "-0.005em",
                color: "#B57C5E",
                animationDelay: "0.55s",
              }}
            >
              Al resto ci pensa{" "}
              <span
                style={{
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontStyle: "normal",
                  fontWeight: 700,
                  color: "#7C8B6E",
                  letterSpacing: "0.005em",
                }}
              >
                Pronto
              </span>
              .
            </div>

            {/* Accent line caramel */}
            <div
              className="anim-accent"
              style={{
                marginTop: 26,
                width: 120,
                height: 4,
                background: "#B57C5E",
                borderRadius: 2,
                animationDelay: "0.85s",
              }}
            />

            {/* Body description compatta */}
            <p
              className="anim-up"
              style={{
                marginTop: 22,
                fontSize: 19,
                lineHeight: 1.5,
                color: "#1C1C1A",
                maxWidth: 520,
                margin: "22px 0 0 0",
                animationDelay: "1.05s",
              }}
            >
              Tutto quello che oggi fai con cinque programmi diversi,
              <br />
              <span style={{ fontWeight: 700 }}>
                qui lo fai in un&apos;unica app.
              </span>
            </p>
          </div>

          {/* ═══════════════════════════════════════════
              9 BULLET SERVIZI · sotto la descrizione
              ═══════════════════════════════════════════ */}
          <div
            className="absolute"
            style={{ top: 590, left: 60, width: 620 }}
          >
            <div
              className="anim-in"
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.36em",
                color: "#5A6A4D",
                textTransform: "uppercase",
                marginBottom: 16,
                animationDelay: "1.2s",
              }}
            >
              · Cosa fa, esattamente
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 13,
              }}
            >
              {SERVICES.map((s, idx) => (
                <div
                  key={s.label}
                  className="anim-up-slow"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    animationDelay: `${1.4 + idx * 0.3}s`,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: s.featured ? "#B57C5E" : "#7C8B6E",
                      flexShrink: 0,
                      marginTop: 8,
                      boxShadow: s.featured
                        ? "0 0 0 5px rgba(181,124,94,0.14)"
                        : "0 0 0 5px rgba(124,139,110,0.12)",
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: "'Montserrat','Inter',sans-serif",
                        fontSize: 19,
                        fontWeight: 700,
                        color: "#1C1C1A",
                        letterSpacing: "-0.01em",
                        lineHeight: 1.2,
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        color: "#5A5A52",
                        lineHeight: 1.3,
                        marginTop: 2,
                      }}
                    >
                      {s.hint}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              iPhone 17 Pro · DESTRA · schermata vera dell'app
              ═══════════════════════════════════════════ */}
          <div
            className="absolute phone-entrance"
            style={{
              top: 340,
              right: 36,
              width: 302,
              height: 640,
              transformOrigin: "center center",
              animationDelay: "0.6s",
              filter:
                "drop-shadow(0 28px 50px rgba(28,28,26,0.20)) drop-shadow(0 10px 18px rgba(28,28,26,0.12))",
            }}
          >
            <div
              className="phone-float"
              style={{ width: "100%", height: "100%" }}
            >
              {/* Telaio titanio Natural · ultra-thin */}
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 62,
                  background:
                    "linear-gradient(135deg, #B5AB94 0%, #968F7A 25%, #6E6856 60%, #3F3B30 100%)",
                  padding: 4,
                  position: "relative",
                  boxShadow:
                    "inset 0 0 0 1.5px rgba(255,255,255,0.12), inset 0 2px 8px rgba(255,255,255,0.08), inset 0 -4px 14px rgba(0,0,0,0.45)",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 58,
                    background: "#000",
                    padding: 3,
                    position: "relative",
                  }}
                >
                  {/* Pulsanti laterali iPhone 17 Pro */}
                  <div aria-hidden style={{ position: "absolute", left: -6, top: 110, width: 3, height: 30, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", left: -6, top: 155, width: 3, height: 90, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", right: -6, top: 180, width: 3, height: 75, background: "linear-gradient(180deg, #8A8472 0%, #4D4738 100%)", borderRadius: 1.5 }} />
                  <div aria-hidden style={{ position: "absolute", right: -6, top: 270, width: 3, height: 22, background: "linear-gradient(180deg, #B5AB94 0%, #6E6856 100%)", borderRadius: 1.5, boxShadow: "0 0 0 1px rgba(0,0,0,0.4)" }} />

                  {/* Schermo · cream da subito · icona pop con spring */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 55,
                      overflow: "hidden",
                      background:
                        "linear-gradient(180deg, #F4F1E8 0%, #EDE9DC 50%, #E5E0CF 100%)",
                      position: "relative",
                    }}
                  >
                    {/* Dynamic Island */}
                    <div
                      style={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 104,
                        height: 30,
                        background: "#000",
                        borderRadius: 15,
                        zIndex: 6,
                      }}
                    />

                    {/* Status bar */}
                    <div
                      style={{
                        position: "absolute",
                        top: 20,
                        left: 22,
                        right: 22,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1C1C1A",
                        fontFamily:
                          "'SF Pro Display','Helvetica Neue',sans-serif",
                        letterSpacing: "-0.01em",
                        zIndex: 4,
                      }}
                    >
                      <span>9:41</span>
                      <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <svg width="14" height="9" viewBox="0 0 18 11" fill="none">
                          <rect x="0" y="7" width="3" height="4" rx="0.5" fill="#1C1C1A"/>
                          <rect x="5" y="5" width="3" height="6" rx="0.5" fill="#1C1C1A"/>
                          <rect x="10" y="2" width="3" height="9" rx="0.5" fill="#1C1C1A"/>
                          <rect x="15" y="0" width="3" height="11" rx="0.5" fill="#1C1C1A" opacity="0.4"/>
                        </svg>
                        <svg width="12" height="9" viewBox="0 0 16 11" fill="none">
                          <path d="M8 11 L6 9 a3 3 0 0 1 4 0 Z" fill="#1C1C1A"/>
                          <path d="M8 7 a5 5 0 0 1 4 1.5" stroke="#1C1C1A" strokeWidth="1.5" fill="none"/>
                          <path d="M8 3 a9 9 0 0 1 7 2.5" stroke="#1C1C1A" strokeWidth="1.5" fill="none"/>
                        </svg>
                        <svg width="20" height="10" viewBox="0 0 26 12" fill="none">
                          <rect x="0.5" y="0.5" width="22" height="11" rx="2.5" stroke="#1C1C1A" strokeWidth="1" fill="none"/>
                          <rect x="2" y="2" width="19" height="8" rx="1.2" fill="#1C1C1A"/>
                          <rect x="23" y="4" width="2" height="4" rx="0.8" fill="#1C1C1A"/>
                        </svg>
                      </span>
                    </div>

                    {/* App icon centrato · sage tile + P bianca · reveal scale spring */}
                    <div
                      className="app-icon-reveal"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginLeft: "-69px",
                        marginTop: "-76px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 16,
                      }}
                    >
                      <div
                        className="app-icon-pulse-after"
                        style={{
                          width: 138,
                          height: 138,
                          borderRadius: 34,
                          background:
                            "linear-gradient(155deg, #8A9678 0%, #7C8B6E 50%, #6B7A5D 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow:
                            "0 20px 46px rgba(38,52,33,0.32), 0 6px 14px rgba(38,52,33,0.22), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -2px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        <img
                          src="/logo/mark-symbol-white.png"
                          alt=""
                          style={{ width: 96, height: 96, objectFit: "contain" }}
                        />
                      </div>
                      <div
                        className="app-label-reveal"
                        style={{
                          fontFamily:
                            "'SF Pro Display','Helvetica Neue',sans-serif",
                          fontSize: 18,
                          fontWeight: 500,
                          color: "#1C1C1A",
                          letterSpacing: "-0.005em",
                        }}
                      >
                        Pronto
                      </div>
                    </div>

                    {/* Home indicator */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 13,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 120,
                        height: 4,
                        background: "#1C1C1A",
                        borderRadius: 2,
                        opacity: 0.6,
                        zIndex: 6,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════
              FOOTER · App Store CTA · sotto a tutto
              ═══════════════════════════════════════════ */}
          <div
            className="absolute anim-up"
            style={{
              bottom: 50,
              left: 60,
              right: 60,
              paddingTop: 22,
              borderTop: "1px solid rgba(28,28,26,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              animationDelay: "2.0s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
              <a
                href={APP_STORE_URL}
                style={{ display: "inline-block", textDecoration: "none" }}
              >
                <img
                  src="/badges/app-store-it.svg"
                  alt="Scarica su App Store"
                  style={{ width: 260, height: "auto", display: "block" }}
                />
              </a>
              <div>
                <div
                  style={{
                    fontFamily: "'Montserrat','Inter',sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#1C1C1A",
                    letterSpacing: "-0.005em",
                    lineHeight: 1.15,
                  }}
                >
                  Scarica gratis
                </div>
                <div
                  style={{
                    fontSize: 15,
                    color: "#6B6A65",
                    marginTop: 2,
                  }}
                >
                  In promo di lancio · senza carta
                </div>
              </div>
            </div>
            <div
              style={{
                fontFamily: "'Montserrat','Inter',sans-serif",
                fontSize: 20,
                fontWeight: 700,
                color: "#1C1C1A",
                letterSpacing: "-0.005em",
              }}
            >
              prontoimmo.it
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          PULSANTI DOWNLOAD · visibili fuori clean mode
          ═══════════════════════════════════════════ */}
      {!cleanMode && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 10,
            zIndex: 100,
          }}
        >
          <button
            onClick={downloadPng}
            disabled={exporting}
            data-testid="download-png-button"
            style={{
              padding: "10px 18px",
              background: "#FAFAF7",
              color: "#1C1C1A",
              border: "1px solid rgba(28,28,26,0.16)",
              borderRadius: 999,
              fontFamily: "'Montserrat','Inter',sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: exporting ? "wait" : "pointer",
              opacity: exporting ? 0.5 : 1,
              boxShadow: "0 8px 22px rgba(0,0,0,0.22)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
          >
            {exporting ? "..." : "Scarica PNG (post)"}
          </button>
          <button
            onClick={downloadMp4}
            disabled={exporting}
            data-testid="download-mp4-button"
            style={{
              padding: "10px 18px",
              background: "#1C1C1A",
              color: "#FAFAF7",
              border: "1px solid #1C1C1A",
              borderRadius: 999,
              fontFamily: "'Montserrat','Inter',sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: exporting ? "wait" : "pointer",
              opacity: exporting ? 0.5 : 1,
              boxShadow: "0 8px 22px rgba(0,0,0,0.36)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
          >
            {exporting ? "Generando..." : "Scarica MP4 (Reel)"}
          </button>
        </div>
      )}
    </div>
  );
}
